import { useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import usePostPlanSettings from './requests/usePostPlanSettings'

const PLAN_SETTINGS_SAVE_DEBOUNCE_MS = 2000
const PLAN_SETTINGS_PENDING_POLL_MS = 100

type PlanSettingsStateIndicatorType = 'unsaved' | 'loading' | 'error' | undefined

const useDebouncedPlanSettingsSave = () => {
  const {
    mutateAsync: postPlanSettings,
    isLoading: isLoadingPostPlanSettings,
    error: postPlanSettingsError,
  } = usePostPlanSettings()

  const debouncedPostPlanSettings = useDebouncedCallback(
    postPlanSettings,
    PLAN_SETTINGS_SAVE_DEBOUNCE_MS,
  )

  const [unsavedChanges, setUnsavedChanges] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setUnsavedChanges(debouncedPostPlanSettings.isPending())
    }, PLAN_SETTINGS_PENDING_POLL_MS)

    return () => clearInterval(interval)
  }, [debouncedPostPlanSettings])

  const stateIndicatorType = useMemo((): PlanSettingsStateIndicatorType => {
    if (unsavedChanges) {
      return 'unsaved'
    }
    if (isLoadingPostPlanSettings) {
      return 'loading'
    }
    if (postPlanSettingsError) {
      return 'error'
    }
    return undefined
  }, [isLoadingPostPlanSettings, postPlanSettingsError, unsavedChanges])

  return {
    onPlanSettingsChange: debouncedPostPlanSettings,
    flushPlanSettings: () => debouncedPostPlanSettings.flush(),
    stateIndicatorType,
  }
}

export type { PlanSettingsStateIndicatorType }
export default useDebouncedPlanSettingsSave
